import { useEffect, useState } from "react";
import { Button } from "../../components/ui/Button";
import { Dialog } from "../../components/ui/Dialog";
import { Input } from "../../components/ui/Input";
import { Switch } from "../../components/ui/Switch";
import { useToast } from "../../components/ui/Toast";
import { LabelWithHint } from "../../components/ui/Tooltip";
import { END_OF_DAY, scheduleToIso, toSchedule, zonedDay, type Schedule } from "../../lib/dates";
import type { ContentItem, MediaAsset } from "../../lib/types";
import { ImageSlot, MediaPicker } from "../media/MediaPicker";
import { useShopTimeZone } from "../settings/useSettings";
import { ScheduleField } from "./ScheduleField";
import { useCreateContent, useUpdateContent, type ContentDraft } from "./useContent";

type Slot = "desktop" | "mobile";

const emptyDraft = (): ContentDraft => ({
	enabled: true,
	title: "",
	altText: null,
	desktopAssetId: null,
	mobileAssetId: null,
	startsAt: null,
	endsAt: null,
});

const EMPTY_SCHEDULE: Schedule = { range: undefined, startHour: 0, endHour: END_OF_DAY };

const ALT_HELP =
	"Read aloud by screen readers and shown if the image fails to load. Describe the offer, not the artwork, like “Spend $70, get free socks”.";

type PopupFormProps = {
	campaignId: string;
	campaignStartsAt: string | null;
	campaignEndsAt: string | null;
	item: ContentItem | null;
	open: boolean;
	onClose: () => void;
};

export const PopupForm = ({
	campaignId,
	campaignStartsAt,
	campaignEndsAt,
	item,
	open,
	onClose,
}: PopupFormProps) => {
	const create = useCreateContent(campaignId, "popup");
	const update = useUpdateContent(campaignId, "popup");
	const toast = useToast();
	const { timeZone, fromSettings } = useShopTimeZone();

	const [draft, setDraft] = useState<ContentDraft>(emptyDraft);
	const [picking, setPicking] = useState<Slot | null>(null);
	const [schedule, setSchedule] = useState<Schedule>(EMPTY_SCHEDULE);
	const [wholeCampaign, setWholeCampaign] = useState(true);

	useEffect(() => {
		if (!open) return;
		setPicking(null);

		if (!item) {
			setDraft(emptyDraft());
			setSchedule(EMPTY_SCHEDULE);
			setWholeCampaign(true);
			return;
		}

		setDraft({
			enabled: item.enabled,
			title: item.title,
			altText: item.altText,
			desktopAssetId: item.desktopAssetId,
			mobileAssetId: item.mobileAssetId,
			startsAt: item.startsAt,
			endsAt: item.endsAt,
		});
		setSchedule(toSchedule(item.startsAt, item.endsAt, timeZone));
		setWholeCampaign(!item.startsAt && !item.endsAt);
	}, [open, item, timeZone]);

	const campaignStart = zonedDay(campaignStartsAt, timeZone);
	const campaignEnd = zonedDay(campaignEndsAt, timeZone);
	const saving = create.isPending || update.isPending;
	const ready = draft.title.trim().length > 0 && Boolean(draft.desktopAssetId);

	const pick = (asset: MediaAsset) => {
		if (!picking) return;
		setDraft({
			...draft,
			[picking === "desktop" ? "desktopAssetId" : "mobileAssetId"]: asset.id,
		});
		setPicking(null);
	};

	const submit = () => {
		if (!ready) return;
		const payload = {
			...draft,
			title: draft.title.trim(),
			...(wholeCampaign ? { startsAt: null, endsAt: null } : scheduleToIso(schedule, timeZone)),
		};
		const onError = (error: unknown) => toast.push((error as Error).message, "error");

		if (item) {
			update.mutate(
				{ itemId: item.id, ...payload },
				{
					onSuccess: () => {
						toast.push("Popup saved");
						onClose();
					},
					onError,
				},
			);
			return;
		}

		create.mutate(payload, {
			onSuccess: () => {
				toast.push("Popup added");
				onClose();
			},
			onError,
		});
	};

	return (
		<>
			<Dialog
				open={open && picking === null}
				onOpenChange={(next) => !next && onClose()}
				title={item ? "Edit popup" : "Add popup"}
				description="Images are converted to WebP before upload."
				footer={
					<>
						<Button variant="outline" onClick={onClose}>
							Cancel
						</Button>
						<Button onClick={submit} disabled={!ready || saving}>
							{saving ? "Saving…" : "Save popup"}
						</Button>
					</>
				}
			>
				<div className="space-y-4">
					<label className="block space-y-1.5">
						<LabelWithHint label="Name">
							Internal label so you can tell phases apart, like “Ends tomorrow”.
						</LabelWithHint>
						<Input
							autoFocus
							value={draft.title}
							placeholder="Happening now"
							onChange={(event) => setDraft({ ...draft, title: event.target.value })}
						/>
					</label>

					<div className="grid gap-4 sm:grid-cols-2">
						<ImageSlot
							assetId={draft.desktopAssetId}
							label="Desktop image"
							onPick={() => setPicking("desktop")}
							onClear={
								draft.desktopAssetId
									? () => setDraft({ ...draft, desktopAssetId: null })
									: undefined
							}
						/>
						<ImageSlot
							assetId={draft.mobileAssetId}
							label="Mobile image (optional)"
							onPick={() => setPicking("mobile")}
							onClear={
								draft.mobileAssetId ? () => setDraft({ ...draft, mobileAssetId: null }) : undefined
							}
						/>
					</div>

					<label className="block space-y-1.5">
						<LabelWithHint label="Alt text">{ALT_HELP}</LabelWithHint>
						<Input
							value={draft.altText ?? ""}
							placeholder="Spend $70 and get free socks"
							onChange={(event) => setDraft({ ...draft, altText: event.target.value })}
						/>
					</label>

					<div className="space-y-3 border-t border-line pt-4">
						<Switch
							checked={wholeCampaign}
							onChange={(event) => setWholeCampaign(event.target.checked)}
							label="Show for the whole campaign"
							hint="Turn off to run this popup for part of the sale only."
						/>
						<ScheduleField
							value={schedule}
							onChange={setSchedule}
							timeZone={timeZone}
							fromSettings={fromSettings}
							disabled={wholeCampaign}
							min={campaignStart}
							max={campaignEnd}
							placeholder={campaignStart ? "Pick a date range" : "Set campaign dates first"}
						/>
						{!wholeCampaign && !campaignStart ? (
							<p className="text-xs text-ink-muted">
								This campaign has no dates yet, so a popup window cannot be constrained to it.
							</p>
						) : null}
					</div>
				</div>
			</Dialog>

			<MediaPicker
				open={picking !== null}
				onOpenChange={(next) => !next && setPicking(null)}
				onSelect={pick}
			/>
		</>
	);
};
